import { getWebWindow } from 'app/utils/web-window';

const GUEST_RETURN_LOCATION_KEY = 'partage-club.guest-return-location';
const MAX_RETURN_LOCATION_AGE_MS = 24 * 60 * 60 * 1000;

interface GuestReturnLocation {
  href: string;
  createdAt: number;
}

function isOnboardingLocation(href: string): boolean {
  const pathname = href.split(/[?#]/, 1)[0] ?? '';
  const isOnboardingPath = (path: string) => /^\/onboarding(?:\/|$)/.test(path);
  if (isOnboardingPath(pathname)) return true;

  try {
    return isOnboardingPath(decodeURIComponent(pathname));
  } catch {
    return true;
  }
}

function getLandingPageHrefForBareBillboard(href: string): string | undefined {
  const [pathAndSearch = '', hash = ''] = href.split('#', 2);
  const [pathname = '', search = ''] = pathAndSearch.split('?', 2);

  if (hash || !/\/(?:billboard\/)?all\/?$/.test(pathname)) return undefined;

  const searchParams = new URLSearchParams(search);
  const onlyDefaultOfferTab = [...searchParams].every(
    ([key, value]) => (key === 'activeTab' || key === 'type') && value === 'offer',
  );
  if (!onlyDefaultOfferTab) return undefined;

  return pathname.replace(/all\/?$/, 'landing-page');
}

export function captureGuestReturnLocation() {
  const window = getWebWindow();
  if (!window || typeof sessionStorage === 'undefined') return;
  if (isOnboardingLocation(window.location.pathname)) return;
  const location: GuestReturnLocation = {
    href: `${window.location.pathname}${window.location.search}${window.location.hash}`,
    createdAt: Date.now(),
  };
  sessionStorage.setItem(GUEST_RETURN_LOCATION_KEY, JSON.stringify(location));
}

export function restoreGuestReturnLocation(): boolean {
  const window = getWebWindow();
  if (!window || typeof sessionStorage === 'undefined') return false;
  const stored = sessionStorage.getItem(GUEST_RETURN_LOCATION_KEY);
  if (!stored) return false;
  sessionStorage.removeItem(GUEST_RETURN_LOCATION_KEY);

  try {
    const location = JSON.parse(stored) as GuestReturnLocation;
    if (
      typeof location.href !== 'string' ||
      !location.href.startsWith('/') ||
      location.href.startsWith('//') ||
      isOnboardingLocation(location.href) ||
      Date.now() - location.createdAt > MAX_RETURN_LOCATION_AGE_MS
    ) {
      return false;
    }
    window.location.replace(getLandingPageHrefForBareBillboard(location.href) ?? location.href);
    return true;
  } catch {
    return false;
  }
}
